/*
 * Copyright (c) by INFODATA-GROUP. All rights reserved.
 * No part of this publication may be reproduced, distributed,
 * or transmitted in any form or by any means, including photocopying, recording,
 * or other electronic or mechanical methods, without the prior written permission of the publisher,
 * except in the case of brief quotations embodied in critical
 * reviews and certain other noncommercial uses permitted by copyright law.
 *  author : vhe
 *  date : 02/03/2021 07:39
 */

import { EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { jsPDF } from 'jspdf';
import { Observable } from 'rxjs';
import { DataModel } from '../engine/data-model';
import { MimeTypes } from '../engine/mime-types';
import { ModelProperty } from '../engine/model-property';
import { Entity } from '../model/entity/entity';
import { PdfEntity } from '../model/entity/pdf-entity';
import { GroupEntity } from '../tool/group-entity';
import { NameValue } from '../tool/name-value';
import { ExportUtils } from './export-utils';
import { Cell } from './model/cell';
import { PdfHeader } from './pdf-header';
import { Roboto } from './roboto';

import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

/* istanbul ignore else */
if (dayjs) {
  dayjs.extend(localizedFormat);
}

export class PdfExportTool {
  private doc: jsPDF;
  public logoPath: string;
  public logo: any;
  public title: string;
  public author: string | NameValue;
  public model: DataModel;
  public columns: ModelProperty[];
  public pageCount = 0;
  public orientation: 'l' | 'p';
  public id: string;
  private detailsCharMax = 210;
  public maxHeight: number;
  public columnWidth: number;
  private readonly trl: TranslateService;
  public constructor(
    trl: TranslateService,
    logoPath: string,
    title: string,
    author: string | NameValue,
    model: DataModel,
    columns: ModelProperty[],
    id: string,
    orientation?: 'l' | 'p',
  ) {
    this.logoPath = logoPath;
    this.columns = columns;
    this.trl = trl;
    this.title = title;
    this.author = author;
    this.model = model;
    this.id = id;
    this.orientation = orientation;
    this.checkColumn();
  }

  public exportArray(
    data: any[],
    detailView?: boolean,
    isGrouped?: boolean,
    downloadDirect = true,
  ): Observable<string> {
    this.doc = new jsPDF(this.orientation ? this.orientation : 'p', 'pt', 'a4'); // A4 size page of PDF
    this.doc.addFileToVFS('Roboto-Regular.ttf', Roboto.regular);
    this.doc.addFileToVFS('Roboto-Bold.ttf', Roboto.bold);
    this.doc.addFont('Roboto-Regular.ttf', 'roboto', 'normal');
    this.doc.addFont('Roboto-Bold.ttf', 'roboto', 'bold');
    this.doc.setFont('roboto');
    const emitter = new EventEmitter<string>();
    if (this.logoPath) {
      this.logo = new Image(132, 60);
      this.logo.src = this.logoPath;
      this.logo.onload = () => {
        // this.imageLoader.emit();
        this.generateHeader();
        this.generateTable(this.model, data, detailView, isGrouped);
        if (downloadDirect) {
          this.doc.save(`${new Date().getTime()}-${this.title}.pdf`); // Generated PDF
        }
        emitter.emit(this.doc.output('datauristring'));
      };
    }
    return emitter.asObservable();
  }

  /**
   * Generate PDF header with title, date, author, page and logo
   */
  private generateHeader(): PdfHeader {
    /* istanbul ignore else */
    if (this.logoPath) {
      const size = this.doc.getFontSize();
      const extension = MimeTypes.getExtension(this.logoPath);
      // Draw header rect
      this.doc.setDrawColor(47, 55, 58);
      this.doc.setTextColor(47, 55, 58);
      this.doc.rect(30, 30, 142.5, 60); // Rect logo
      this.doc.setFontSize(9);
      this.doc.setFont('roboto', 'bold');
      if (this.id) {
        this.doc.rect(172.5, 30, 50, 30); // Rect Id
        this.doc.text(this.id.toUpperCase(), 178.5, 47); // ID
        this.doc.rect(
          222.5,
          30,
          this.doc.internal.pageSize.getWidth() - 252.5,
          30,
        ); // Rect Title
        this.doc.text(this.title, 228.5, 47); // Title
      } else {
        this.doc.rect(
          172.5,
          30,
          this.doc.internal.pageSize.getWidth() - 202.5,
          30,
        ); // Rect Title
        this.doc.text(this.title, 189.5, 50); // Title
      }
      this.doc.rect(
        172.5,
        60,
        this.doc.internal.pageSize.getWidth() - 202.5,
        30,
      ); // Rect author
      this.doc.rect(this.doc.internal.pageSize.getWidth() - 130, 30, 100, 30); // Rect page
      this.doc.rect(this.doc.internal.pageSize.getWidth() - 130, 60, 100, 30); // Rect date

      this.doc.addImage(
        this.logo,
        extension.toUpperCase(),
        45,
        37.5,
        105.6,
        40,
      );
      this.doc.text(
        `Page ${this.pageCount + 1}`,
        this.doc.internal.pageSize.getWidth() - 124,
        48,
      ); // Page count
      this.doc.setFont('roboto', 'normal');
      this.doc.text(
        dayjs().format('DD/MM/YYYY - HH:mm'),
        this.doc.internal.pageSize.getWidth() - 124,
        78,
      ); // Date
      if (this.author) {
        if (this.author instanceof String) {
          this.doc.text(this.author.toString(), 178.5, 78); // Author
        } else if (this.author instanceof NameValue) {
          const dimension = this.doc.getTextDimensions(this.author.name);
          this.doc.setFont('roboto', 'bold');
          this.doc.text(this.author.name, 178.5, 78);
          this.doc.setFont('roboto', 'normal');
          this.doc.text(': ' + this.author.value, 178.5 + dimension.w + 4, 78);
        }
      }
      this.doc.setFontSize(size);
      return {
        id: this.id,
        subtitle: this.author,
        logo: {
          image: this.logo,
          extension: MimeTypes.getExtension(this.logoPath),
        },
        date: dayjs().format('DD/MM/YYYY - HH:mm'),
        title: this.title,
        height: 100,
      };
    }
    /* istanbul ignore next */
    return undefined;
  }

  /**
   * Generate Pdf DataTable header and body. When new page is create, recreate the header.
   * @param model list of DataModel Property
   * @param data list of Entity
   * @param detailView view of Detail
   * @param isGrouped true if table is grouped
   */
  private generateTable(
    model: DataModel,
    data: any[],
    detailView: boolean,
    isGrouped: boolean,
  ) {
    const columnSize =
      (this.doc.internal.pageSize.getWidth() - 60) / this.columns.length;
    this.columnWidth = columnSize - 6;
    let charMax = Math.floor((columnSize - 4) / 4.3);
    charMax = charMax > 0 ? charMax : 1;
    this.doc.setFontSize(7);
    this.doc.setDrawColor(47, 55, 58);
    this.generateTableHeader(this.columns, columnSize, charMax);
    charMax = Math.floor((columnSize - 4) / 4.6);
    this.doc.setFontSize(8);
    const detail =
      detailView && model.detailViewProperty ? model.detailViewProperty : null;
    if (!isGrouped) {
      this.generateTableBody(
        this.columns,
        data,
        columnSize,
        charMax,
        120,
        detail,
      );
    } else {
      this.generateGroupTableBody(
        this.columns,
        data,
        columnSize,
        charMax,
        120,
        detail,
      );
    }
  }

  /**
   * Generate header of Pdf Table
   * @param columns list of property (column)
   * @param columnSize size of each column
   * @param charMax max char on the row
   */
  private generateTableHeader(
    columns: ModelProperty[],
    columnSize: number,
    charMax: number,
  ) {
    this.doc.setFont('roboto', 'bold');
    const x = 30;
    let position = x;
    const y = 100;
    for (const property of columns) {
      this.doc.setFillColor(250, 250, 250);
      this.doc.rect(position, y, columnSize, 20, 'F');
      this.doc.text(
        ExportUtils.truncate(property.title, charMax).toUpperCase(),
        position + 3,
        y + 13,
      );
      position += columnSize;
    }
    this.doc.setDrawColor(203, 203, 203);
    this.doc.setLineWidth(1.5);
    this.doc.line(x, y + 20, this.doc.internal.pageSize.getWidth() - x, y + 20);
    this.doc.setFont('roboto', 'normal');
  }

  /**
   * Generate body of dataTable PDF. Manage new page and recreation of header for each page
   * @param columns properties of DataModel
   * @param data list of data
   * @param columnSize width of each column
   * @param charMax max char for one row on one column
   * @param y ordinate position on the page
   * @param detailView true if do print detail
   */
  private generateTableBody(
    columns: ModelProperty[],
    data: any[],
    columnSize: number,
    charMax: number,
    y: number,
    detailView: ModelProperty,
  ) {
    let drawBackground = false;
    for (const d of data) {
      const row = ExportUtils.convertDataToRow(d, columns);
      const rowHeight = this.getRowHeight(row) * 11 + 8;
      let detailsHeight = 0;
      let details = null;
      if (detailView) {
        details = d[detailView.id];
        if (details) {
          if (details instanceof Array) {
            let detailsRowCount = 1;
            for (const det of details) {
              detailsRowCount += Math.ceil(det.length / this.detailsCharMax);
            }
            detailsHeight = detailsRowCount * 11;
          } else {
            detailsHeight =
              Math.ceil(details.length / this.detailsCharMax) * 11;
          }
        }
      }
      if (
        y + rowHeight + detailsHeight >=
        this.doc.internal.pageSize.getHeight() - 30
      ) {
        this.addPage();
        this.doc.setFontSize(7);
        this.generateTableHeader(columns, columnSize, charMax);
        this.doc.setFontSize(8);
        this.doc.setDrawColor(47, 55, 58);
        y = 120;
      }
      let x = 30;
      for (const cell of row) {
        this.doc.setFillColor(243, 243, 243);
        if (drawBackground && !detailView) {
          this.doc.rect(x, y, columnSize, rowHeight, 'F');
        }
        const width = this.doc.getTextWidth(cell.text);
        if (width > this.columnWidth) {
          this.generateMultilineCell(cell, x, y);
        } else {
          this.setAlignedText(cell.text, cell.align, x + 3, y + 13, width);
        }
        x += columnSize;
      }
      y += rowHeight;
      if (details) {
        this.doc.setFillColor(243, 243, 243);
        this.doc.rect(
          30,
          y,
          this.doc.internal.pageSize.getWidth() - 60,
          detailsHeight,
          'F',
        );
        if (details instanceof Array) {
          for (const det of details) {
            const array = this.convertDetailsToArray(det);
            for (const line of array) {
              this.doc.text(line, 34, y + 13);
              y += 11;
            }
          }
          y += 6;
        } else {
          const array = this.convertDetailsToArray(details);
          for (const line of array) {
            this.doc.text(line, 34, y + 13);
            y += 11;
          }
        }
        y += 6;
      }
      drawBackground = !drawBackground;
    }
  }

  private generateGroupTableBody(
    columns: ModelProperty[],
    groups: GroupEntity<Entity>[],
    columnSize: number,
    charMax: number,
    y: number,
    detailView: ModelProperty,
  ) {
    for (const group of groups) {
      if (y + 24 >= this.doc.internal.pageSize.getHeight() - 30) {
        this.addPage();
        this.doc.setFontSize(7);
        this.generateTableHeader(columns, columnSize, charMax);
        this.doc.setFontSize(8);
        this.doc.setDrawColor(47, 55, 58);
        y = 120;
      }
      this.doc.setFillColor(74, 85, 92);
      this.doc.rect(30, y, this.doc.internal.pageSize.getWidth() - 60, 24, 'F');
      this.doc.setTextColor(255, 255, 255);
      this.doc.text(group.value.toString(), 35, y + 15);
      this.doc.setTextColor(47, 55, 58);
      y += 24;
      for (const d of group.items) {
        const row = ExportUtils.convertDataToRow(d.entity, columns);
        const rowHeight = this.getRowHeight(row) * 11 + 8;
        let detailsHeight = 0;
        let details = null;
        if (detailView) {
          details = d[detailView.id];
          if (details) {
            if (details instanceof Array) {
              detailsHeight = details.length * 11 + 8;
            } else {
              detailsHeight = 11;
            }
          }
        }
        if (
          y + rowHeight + detailsHeight >=
          this.doc.internal.pageSize.getHeight() - 30
        ) {
          this.addPage();
          this.doc.setFontSize(7);
          this.generateTableHeader(columns, columnSize, charMax);
          this.doc.setFontSize(8);
          this.doc.setDrawColor(47, 55, 58);
          y = 120;
        }
        let x = 30;
        for (const cell of row) {
          this.doc.setFillColor(243, 243, 243);
          if (cell.text.length > charMax) {
            this.generateMultilineCell(cell, x, y);
          } else {
            this.doc.text(cell.text, x + 3, y + 13);
          }
          x += columnSize;
        }
        y += rowHeight;
        if (details) {
          this.doc.setFillColor(243, 243, 243);
          this.doc.rect(
            30,
            y,
            this.doc.internal.pageSize.getWidth() - 60,
            detailsHeight,
            'F',
          );
          if (details instanceof Array) {
            for (const det of details) {
              this.doc.text(det.toString(), 30, y + 13);
              y += 11;
            }
          } else {
            this.doc.text(details.toString(), 13, y + 13);
          }
          y += 8;
        }
      }
    }
  }
  /**
   * If text size is bigger than char max
   * @param cell to split on multiline
   * @param x position
   * @param y position
   */
  private generateMultilineCell(cell, x, y) {
    let tempY = y + 13;
    let length = cell.text.length;
    while (length > 0) {
      const sub = this.getOptimizedString(cell);
      if (sub.length === 0) {
        break;
      }
      length -= sub.length;
      cell.text = cell.text.substr(sub.length);
      this.setAlignedText(
        sub,
        cell.align,
        x + 3,
        tempY,
        this.doc.getTextWidth(sub),
      );
      // this.doc.text(sub, x + 3, tempY);
      tempY += 11;
    }
    if (cell.text.length > 0) {
      this.setAlignedText(
        cell.text,
        cell.align,
        x + 3,
        tempY,
        this.doc.getTextWidth(cell.text),
      );
    }
  }

  /**
   * Get row height of cell (one row = 1, multi row = x)
   * @param row array of cell value
   */
  private getRowHeight(row: { text: any; align: string }[]) {
    let height = 1;
    for (const cell of row) {
      const width = this.doc.getTextWidth(cell.text);
      if (width > this.columnWidth) {
        const ratio = Math.ceil(width / this.columnWidth);
        if (ratio > height) {
          height = ratio;
        }
      }
    }
    return height;
  }

  /**
   * Add page to the pdf Doc and increment pageCount
   */
  private addPage() {
    this.doc.addPage();
    ++this.pageCount;
    this.generateHeader();
  }

  public exportEntity(entity: PdfEntity) {
    this.doc = new jsPDF(this.orientation ? this.orientation : 'p', 'pt', 'a4'); // A4 size page of PDF
    this.doc.addFileToVFS('Roboto-Regular.ttf', Roboto.regular);
    this.doc.addFileToVFS('Roboto-Bold.ttf', Roboto.bold);
    this.doc.addFont('Roboto-Regular.ttf', 'roboto', 'normal');
    this.doc.addFont('Roboto-Regular.ttf', 'roboto', 'bold');
    this.maxHeight = this.doc.internal.pageSize.getHeight() - 30;
    if (this.logoPath) {
      this.logo = new Image(132, 60);
      this.logo.src = this.logoPath;
      this.logo.onload = () => {
        // this.imageLoader.emit();
        const header = this.generateHeader();
        this.generateBody(entity, header);
        this.doc.save(`${new Date().getTime()}-${this.title}.pdf`); // Generated PDF
      };
    }
  }

  public generateBody(entity: PdfEntity, header: PdfHeader) {
    let y = 100;
    for (const section of entity.getExportSection()) {
      const dimen = section.getDimension(this.doc);
      if (y + dimen.h > this.maxHeight) {
        this.addPage();
        y = 100;
      }
      y = section.build(this.trl, this.doc, header, 30, y) + 20;
    }
  }

  private convertDetailsToArray(det: string): string[] {
    const array = [];
    let index = 0;
    while (index < det.length) {
      array.push(det.slice(index, index + this.detailsCharMax).trim());
      index += this.detailsCharMax;
    }
    return array;
  }

  private getOptimizedString(cell: Cell): string {
    if (this.doc.getTextWidth(cell.text) < this.columnWidth) {
      return cell.text;
    } else {
      let str = '';
      let index = 0;
      while (this.doc.getTextWidth(str) < this.columnWidth) {
        str += cell.text.charAt(index);
        ++index;
      }
      return str.slice(0, -1);
    }
  }

  /**
   * Add text with a specific alignment
   * @param text
   * @param align
   * @param x
   * @param y
   * @param textWidth
   * @private
   */
  private setAlignedText(
    text: string,
    align: string,
    x: number,
    y: number,
    textWidth: number,
  ): void {
    if (align === 'right' && text[text.length - 1] === ' ') {
      text = text.trim();
      textWidth = this.doc.getTextWidth(text);
    }

    switch (align) {
      case 'center':
        this.doc.text(text, x + (this.columnWidth / 2 - textWidth / 2), y);
        break;
      case 'right':
        this.doc.text(text, x + (this.columnWidth - textWidth), y);
        break;
      case 'left':
        this.doc.text(text, x, y);
        break;
    }
  }

  private checkColumn() {
    const columns: ModelProperty[] = [];
    this.columns.forEach((c) => {
      if (c.showOnExport) {
        if (c.replaceOnExport) {
          const replaceColumn = this.model.find(c.replaceOnExport);
          if (replaceColumn) {
            columns.push(replaceColumn);
          } else {
            columns.push(c);
          }
        } else {
          columns.push(c);
        }
      }
    });
    this.columns = columns;
  }
}
